import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Link } from "react-router-dom";
import { CreditCard, RefreshCw, Mail, BarChart3 } from "lucide-react";

const Row = ({ label, value, ok, testid }) => (
  <div className="flex items-start justify-between gap-4 py-2" style={{ borderBottom: "1px solid #27272A" }}>
    <span className="eyebrow shrink-0">{label}</span>
    <span className="text-xs text-right break-all" style={{ color: ok === false ? "#FF0055" : "#d4d4d8" }} data-testid={testid}>{value}</span>
  </div>
);

export const PayPalAdminCard = () => {
  const { user } = useAuth();
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState("");

  const load = () => api.get("/admin/paypal/status").then((r) => setS(r.data)).catch(() => {});
  useEffect(() => { if (user?.role === "admin") load(); }, [user]);

  if (user?.role !== "admin" || !s) return null;

  const setup = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/admin/paypal/setup");
      setMsg(data.steps.length ? `Done: ${data.steps.join(", ").replace(/_/g, " ")}` : "Already set up — nothing to change.");
      await load();
    } catch (e) {
      setMsg(e.response?.data?.detail || "Setup failed.");
    }
    setBusy(false);
  };

  const testWelcome = async () => {
    setBusy(true);
    setMsg("");
    try {
      const { data } = await api.post("/admin/paypal/test-welcome");
      setMsg(`Welcome email preview sent to ${data.to}.`);
    } catch (e) {
      setMsg(e.response?.data?.detail || "Send failed.");
    }
    setBusy(false);
  };

  const ready = s.credentials_ok && s.plan_id && s.webhook_id;

  return (
    <section className="mt-12" data-testid="paypal-admin-card">
      <p className="eyebrow">Admin · Payments</p>
      <h2 className="font-display font-black text-2xl md:text-3xl mt-1 flex items-center justify-between gap-4 flex-wrap">
        <span>PAYPAL <span style={{ color: s.mode === "live" ? "#FF0055" : "#a1a1aa" }} data-testid="paypal-mode">{s.mode.toUpperCase()}</span></span>
        <Link to="/admin" className="btn-secondary text-xs flex items-center gap-2" data-testid="profile-admin-dashboard-link"><BarChart3 size={14} /> Member numbers</Link>
      </h2>
      <div className="card mt-6">
        <Row label="Credentials" value={s.credentials_ok ? `Valid (${s.client_id_hint})` : "Invalid for this mode"} ok={s.credentials_ok} testid="paypal-creds" />
        <Row label="Plan" value={s.plan_id ? `${s.plan_id} · ${s.price} ${s.currency}/mo` : "Not created"} ok={!!s.plan_id} testid="paypal-plan" />
        <Row label="Webhook" value={s.webhook_id ? `${s.webhook_id} → ${s.webhook_url}` : "Not registered"} ok={!!s.webhook_id} testid="paypal-webhook" />
        <Row label="App URL" value={s.app_url} testid="paypal-app-url" />
        <p className="text-xs text-zinc-500 mt-4">
          {ready ? "Members can subscribe and cancellations / failed payments update access instantly." : "Run setup to create the plan and register the webhook for the current mode."}
          {s.mode === "sandbox" && " To go live: put Live Client ID + Secret in the backend .env, set PAYPAL_MODE=live, then run setup again."}
        </p>
        <div className="mt-5 flex gap-3 flex-wrap items-center">
          <button className="btn-primary flex items-center gap-2" onClick={setup} disabled={busy || !s.credentials_ok} data-testid="paypal-setup-btn">
            <CreditCard size={14} /> {busy ? "Working…" : ready ? "Re-run setup" : "Set up PayPal"}
          </button>
          <button className="btn-secondary flex items-center gap-2" onClick={testWelcome} disabled={busy} data-testid="paypal-test-welcome-btn">
            <Mail size={14} /> Preview welcome email
          </button>
          <button className="btn-ghost flex items-center gap-2" onClick={load} disabled={busy} data-testid="paypal-refresh-btn">
            <RefreshCw size={14} /> Refresh
          </button>
        </div>
        {msg && <p className="text-xs mt-3 text-zinc-300" data-testid="paypal-admin-msg">{msg}</p>}
      </div>
    </section>
  );
};
